import { useVisiblePolling } from '@/utils/useVisiblePolling';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { 
  Shield, Users, Settings, Database, HeadphonesIcon, Activity, Lock, 
  FileText, BarChart3, TrendingUp, FileQuestion, CreditCard, Package, Navigation, Palette, Languages, RefreshCw, UserPlus, Flag, Stethoscope, Image, Star, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UsersManagement from '@/components/admin/UsersManagement';
import DemoAccountsManager from '@/components/admin/DemoAccountsManager';
import ContentManagement from '@/components/admin/ContentManagement';
import SupportTicketsManagement from '@/components/admin/SupportTicketsManagement';
import PaymentsManagement from '@/components/admin/PaymentsManagement';
import ActivityLogs from '@/components/admin/ActivityLogs';
import RolesPermissions from '@/components/admin/RolesPermissions';
import SecuritySettings from '@/components/admin/SecuritySettings';
import BackupRestore from '@/components/admin/BackupRestore';
import SupportCenter from '@/components/admin/SupportCenter';
import SystemSettings from '@/components/admin/SystemSettings';
import TestManagement from '@/components/admin/TestManagement';
import SubscriptionManagement from '@/components/admin/SubscriptionManagement';
import PricingPlansManager from '@/components/admin/PricingPlansManager';
import SubscriptionPlans from '@/components/admin/SubscriptionPlans';
import TypographySettings from '@/components/admin/TypographySettings';
import ContentCMS from '@/components/admin/ContentCMS';
import UniversalTextSearch from '@/components/admin/UniversalTextSearch';
import PagesManagement from '@/components/admin/PagesManagement';
import RefundRequestsManagement from '@/components/admin/RefundRequestsManagement';
import QuestionReports from '@/components/admin/QuestionReports';
import AnalyticsDashboard from '@/components/admin/AnalyticsDashboard';
import ReviewsManagement from '@/components/admin/ReviewsManagement';
import QuestionAudioPanel from '@/components/admin/QuestionAudioPanel';
import { Volume2 } from 'lucide-react';
import DatabaseCleanup from '@/components/admin/DatabaseCleanup';
import ServerHealth from '@/components/admin/ServerHealth';
import ChatMessagesManagement from '@/components/admin/ChatMessagesManagement';
import LiveChatAdmin from '@/components/admin/LiveChatAdmin';
import ImageSync from '@/components/admin/ImageSync';
import SiteSettingsPanel from '@/components/admin/SiteSettingsPanel';
import QuestionEditHistory from '@/components/admin/QuestionEditHistory';
import SubscribersActivity from '@/components/admin/SubscribersActivity';

import { API } from '@/config';

const AdminPanelPage = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState('overview'); // overview, users, roles, content, logs, security, backup, support, notifications, settings

  useEffect(() => {
    // التحقق من أن المستخدم هو super_admin
    if (!user || user.role !== 'super_admin') {
      toast.error(i18n.language === 'ar' ? 'الوصول مرفوض' : 'Access Denied');
      navigate('/oversikt');
      return;
    }
    fetchAdvancedStats();
  }, [user, navigate, token]);

  const fetchAdvancedStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await Promise.race([
        axios.get(`${API}/admin/advanced-stats`, { headers }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
      ]);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({ total_users: 0, active_today: 0, total_content: 0, total_questions: 0 });
    }
  };

  if (!user || user.role !== 'super_admin') {
    return null;
  }

  const renderContent = () => {
    switch(activeSection) {
      case 'overview':
        return renderOverview();
      case 'users':
        return <UsersManagement token={token} i18n={i18n} />;
      case 'demo-accounts':
        return <DemoAccountsManager token={token} i18n={i18n} />;
      case 'roles':
        return <RolesPermissions i18n={i18n} />;
      case 'content':
        return <ContentManagement token={token} i18n={i18n} />;
      case 'support-tickets':
        return <SupportTicketsManagement token={token} i18n={i18n} />;
      case 'refund-requests':
        return <RefundRequestsManagement token={token} i18n={i18n} />;
      case 'payments':
        return <PaymentsManagement token={token} i18n={i18n} />;
      case 'tests':
        return <TestManagement token={token} i18n={i18n} />;
      case 'subscriptions':
        return <SubscriptionManagement token={token} i18n={i18n} />;
      case 'plans':
        return <SubscriptionPlans token={token} i18n={i18n} />;
      case 'pricing-plans':
        return <PricingPlansManager token={token} i18n={i18n} />;
      case 'logs':
        return <ActivityLogs token={token} i18n={i18n} />;
      case 'security':
        return <SecuritySettings i18n={i18n} />;
      case 'backup':
        return <BackupRestore i18n={i18n} />;
      case 'support':
        return <SupportCenter i18n={i18n} />;
      case 'settings':
        return <SystemSettings i18n={i18n} />;
      case 'typography':
        return <TypographySettings i18n={i18n} />;
      case 'content-cms':
        return <ContentCMS i18n={i18n} />;
      case 'universal-text-search':
        return <UniversalTextSearch token={token} i18n={i18n} />;
      case 'pages':
        return <PagesManagement token={token} i18n={i18n} />;
      case 'question-reports':
        return <QuestionReports token={token} i18n={i18n} />;
      case 'analytics':
        return <AnalyticsDashboard token={token} />;
      case 'reviews':
        return <ReviewsManagement token={token} i18n={i18n} />;
      case 'server-health':
        return <ServerHealth token={token} i18n={i18n} />;
      case 'db-cleanup':
        return <DatabaseCleanup token={token} i18n={i18n} />;
      case 'question-audio':
        return <QuestionAudioPanel token={token} i18n={i18n} />;
      case 'chat-messages':
        return <ChatMessagesManagement token={token} i18n={i18n} />;
      case 'live-chat':
        return <LiveChatAdmin token={token} i18n={i18n} />;
      case 'image-sync':
        return <ImageSync token={token} i18n={i18n} />;
      case 'site-settings':
        return <SiteSettingsPanel token={token} i18n={i18n} />;
      case 'question-history':
        return <QuestionEditHistory token={token} i18n={i18n} />;
      case 'subscribers-activity':
        return <SubscribersActivity token={token} i18n={i18n} />;
      default:
        return renderOverview();
    }
  };

  const renderOverview = () => {
    return (
      <>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Users}
              label={i18n.language === 'ar' ? 'إجمالي المستخدمين' : 'Total Users'}
              value={stats?.total_users || 0}
              color="blue"
            />
            <StatCard
              icon={Activity}
              label={i18n.language === 'ar' ? 'نشط اليوم' : 'Active Today'}
              value={stats?.active_today || 0}
              color="emerald"
            />
            <StatCard
              icon={FileText}
              label={i18n.language === 'ar' ? 'إجمالي المحتوى' : 'Total Content'}
              value={stats?.total_content || 0}
              color="purple"
            />
            <StatCard
              icon={TrendingUp}
              label={i18n.language === 'ar' ? 'الأسئلة' : 'Questions'}
              value={stats?.total_questions || 0}
              color="orange"
            />
          </div>

        {/* Admin Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AdminCard
            icon={Users}
            title={i18n.language === 'ar' ? 'إدارة المستخدمين' : 'User Management'}
            desc={i18n.language === 'ar' ? 'إضافة، تعديل، وحذف المستخدمين وتعيين الأدوار' : 'Add, edit, delete users and assign roles'}
            color="blue"
            onClick={() => setActiveSection('users')}
          />
          <AdminCard
            icon={Activity}
            title={i18n.language === 'ar' ? 'نشاط المستخدمين' : i18n.language === 'sv' ? 'Användaraktivitet' : 'User Activity'}
            desc={i18n.language === 'ar' ? 'آخر ظهور للمسجلين والمشتركين ومن متصل الآن' : i18n.language === 'sv' ? 'Senast sedd för registrerade och prenumeranter' : 'Last seen for registered users and subscribers'}
            color="emerald"
            onClick={() => setActiveSection('subscribers-activity')}
          />
          <AdminCard
            icon={UserPlus}
            title={i18n.language === 'ar' ? 'الحسابات التجريبية' : 'Demo Accounts'}
            desc={i18n.language === 'ar' ? 'إنشاء حسابات وهمية للتجربة بإيميلات @drivemaster.se' : 'Create demo accounts with @drivemaster.se emails'}
            color="teal"
            onClick={() => setActiveSection('demo-accounts')}
          />
          <AdminCard
            icon={Shield}
            title={i18n.language === 'ar' ? 'الأدوار والصلاحيات' : 'Roles & Permissions'}
            desc={i18n.language === 'ar' ? 'إدارة صلاحيات المستخدمين والأدوار' : 'Manage user permissions and roles'}
            color="purple"
            onClick={() => setActiveSection('roles')}
          />
          <AdminCard
            icon={FileText}
            title={i18n.language === 'ar' ? 'إدارة المحتوى' : 'Content Management'}
            desc={i18n.language === 'ar' ? 'إدارة الدروس، الأسئلة، والفيديوهات' : 'Manage lessons, questions, and videos'}
            color="emerald"
            onClick={() => setActiveSection('content')}
          />
          <AdminCard
            icon={HeadphonesIcon}
            title={i18n.language === 'ar' ? 'رسائل الدعم' : i18n.language === 'sv' ? 'Supportärenden' : 'Support Tickets'}
            desc={i18n.language === 'ar' ? 'عرض وإدارة رسائل الدعم من المستخدمين' : i18n.language === 'sv' ? 'Visa och hantera supportärenden' : 'View and manage support messages'}
            color="purple"
            onClick={() => setActiveSection('support-tickets')}
          />
          <AdminCard
            icon={RefreshCw}
            title={i18n.language === 'ar' ? 'طلبات الاسترداد' : i18n.language === 'sv' ? 'Återbetalningar' : 'Refund Requests'}
            desc={i18n.language === 'ar' ? 'مراجعة والموافقة على طلبات استرداد الأموال' : i18n.language === 'sv' ? 'Granska och godkänn återbetalningar' : 'Review and approve refund requests'}
            color="amber"
            onClick={() => setActiveSection('refund-requests')}
          />
          <AdminCard
            icon={CreditCard}
            title={i18n.language === 'ar' ? 'إدارة المدفوعات' : i18n.language === 'sv' ? 'Betalningar' : 'Payments'}
            desc={i18n.language === 'ar' ? 'عرض وتتبع جميع المعاملات المالية' : i18n.language === 'sv' ? 'Visa och spåra alla betalningar' : 'View and track payment transactions'}
            color="green"
            onClick={() => setActiveSection('payments')}
          />
          <AdminCard
            icon={Activity}
            title={i18n.language === 'ar' ? 'سجل النشاطات' : 'Activity Logs'}
            desc={i18n.language === 'ar' ? 'مراقبة نشاط المستخدمين وسجل الدخول' : 'Monitor user activity and login history'}
            color="amber"
            onClick={() => setActiveSection('logs')}
          />
          <AdminCard
            icon={Lock}
            title={i18n.language === 'ar' ? 'الأمان' : 'Security'}
            desc={i18n.language === 'ar' ? 'إعدادات الأمان والحماية من الاختراق' : 'Security settings and intrusion protection'}
            color="red"
            onClick={() => setActiveSection('security')}
          />
          <AdminCard
            icon={Database}
            title={i18n.language === 'ar' ? 'النسخ الاحتياطي' : 'Backup & Restore'}
            desc={i18n.language === 'ar' ? 'نسخ احتياطي واستعادة للبيانات' : 'Data backup and restore'}
            color="indigo"
            onClick={() => setActiveSection('backup')}
          />
          <AdminCard
            icon={HeadphonesIcon}
            title={i18n.language === 'ar' ? 'الدعم الفني' : 'Support Center'}
            desc={i18n.language === 'ar' ? 'إدارة تذاكر الدعم والرسائل' : 'Manage support tickets and messages'}
            color="teal"
            onClick={() => setActiveSection('support')}
          />
          <AdminCard
            icon={Settings}
            title={i18n.language === 'ar' ? 'الإعدادات' : 'System Settings'}
            desc={i18n.language === 'ar' ? 'إعدادات النظام والتخصيص العامة' : 'General system settings and customization'}
            color="slate"
            onClick={() => setActiveSection('settings')}
          />
          <AdminCard
            icon={FileQuestion}
            title={i18n.language === 'ar' ? 'إدارة الاختبارات' : 'Test Management'}
            desc={i18n.language === 'ar' ? 'إنشاء وإدارة الاختبارات المختلفة' : 'Create and manage different tests'}
            color="purple"
            onClick={() => setActiveSection('tests')}
          />
          <AdminCard
            icon={CreditCard}
            title={i18n.language === 'ar' ? 'إدارة الاشتراكات' : 'User Subscriptions'}
            desc={i18n.language === 'ar' ? 'إدارة اشتراكات المستخدمين وتعديلها' : 'Manage user subscriptions and status'}
            color="emerald"
            onClick={() => setActiveSection('subscriptions')}
          />
          <AdminCard
            icon={Package}
            title={i18n.language === 'ar' ? 'خطط الاشتراك' : 'Subscription Plans'}
            desc={i18n.language === 'ar' ? 'إنشاء وتعديل خطط الاشتراك (شهري، سنوي، مجاني)' : 'Create and edit plans (Monthly, Yearly, Free)'}
            color="purple"
            onClick={() => setActiveSection('plans')}
          />
          <AdminCard
            icon={Package}
            title={i18n.language === 'ar' ? 'الباقات والأسعار (جديد)' : 'Prisplaner (ny)'}
            desc={i18n.language === 'ar' ? 'تعديل الأسعار والمدد والشارات، مع حساب التوفير تلقائياً' : 'Ändra pris, längd och märken – besparing räknas automatiskt'}
            color="emerald"
            onClick={() => setActiveSection('pricing-plans')}
          />
          <AdminCard
            icon={FileText}
            title={i18n.language === 'ar' ? '🔍 البحث الشامل عن النصوص' : '🔍 Universal Text Search'}
            desc={i18n.language === 'ar' ? 'ابحث عن أي نص في الموقع وتحكم به: تعديل، إخفاء، حذف، تغيير اللون والحجم' : 'Search any text and control it: edit, hide, delete, change color & size'}
            color="blue"
            onClick={() => setActiveSection('universal-text-search')}
          />
          <AdminCard
            icon={FileText}
            title={i18n.language === 'ar' ? '📄 الصفحات القانونية' : i18n.language === 'sv' ? '📄 Juridiska sidor' : '📄 Legal Pages'}
            desc={i18n.language === 'ar' ? 'تحرير سياسة الخصوصية وشروط الخدمة' : i18n.language === 'sv' ? 'Redigera integritetspolicy och användarvillkor' : 'Edit Privacy Policy & Terms of Service'}
            color="indigo"
            onClick={() => setActiveSection('pages')}
          />
          <AdminCard
            icon={BarChart3}
            title={i18n.language === 'ar' ? 'تقارير الزوار' : i18n.language === 'sv' ? 'Besöksanalys' : 'Visitor Analytics'}
            desc={i18n.language === 'ar' ? 'إحصائيات الزوار، الأجهزة، الصفحات الأكثر زيارة والأخطاء' : i18n.language === 'sv' ? 'Besökarstatistik, enheter, mest besökta sidor och fel' : 'Visitor stats, devices, top pages and errors'}
            color="blue"
            onClick={() => setActiveSection('analytics')}
          />
          <AdminCard
            icon={Activity}
            title={i18n.language === 'ar' ? 'إدارة التقييمات' : i18n.language === 'sv' ? 'Hantera recensioner' : 'Reviews Management'}
            desc={i18n.language === 'ar' ? 'مراجعة تقييمات الطلاب والموافقة عليها أو رفضها' : i18n.language === 'sv' ? 'Granska och godkänn eller avvisa elevrecensioner' : 'Review and approve or reject student reviews'}
            color="amber"
            onClick={() => setActiveSection('reviews')}
          />
          <AdminCard
            icon={Flag}
            title={i18n.language === 'ar' ? 'تقارير الأخطاء' : 'Question Reports'}
            desc={i18n.language === 'ar' ? 'مراجعة تقارير الأخطاء المرسلة من المستخدمين' : 'Review error reports submitted by users'}
            color="orange"
            onClick={() => setActiveSection('question-reports')}
          />
          <AdminCard
            icon={Activity}
            title={i18n.language === 'ar' ? 'محادثات المستخدمين' : i18n.language === 'sv' ? 'Användarkonversationer' : 'User Conversations'}
            desc={i18n.language === 'ar' ? 'عرض جميع محادثات المستخدمين مع مساعد AI' : i18n.language === 'sv' ? 'Visa alla användarkonversationer med AI-assistenten' : 'View all user conversations with the AI assistant'}
            color="cyan"
            onClick={() => setActiveSection('chat-messages')}
          />
          <LiveChatCard
            token={token}
            i18n={i18n}
            onClick={() => setActiveSection('live-chat')}
          />
          <AdminCard
            icon={Stethoscope}
            title={i18n.language === 'ar' ? 'تشخيص السيرفر' : 'Server Diagnostics'}
            desc={i18n.language === 'ar' ? 'فحص صحة السيرفر وقاعدة البيانات والأخطاء' : 'Check server health, database and errors'}
            color="cyan"
            onClick={() => setActiveSection('server-health')}
          />
          <AdminCard
            icon={Database}
            title={i18n.language === 'ar' ? 'تنظيف قاعدة البيانات' : 'Databasrensning'}
            desc={i18n.language === 'ar' ? 'حذف النسخ القديمة غير المستخدمة وإبقاء السجلات لآخر 90 يوماً' : 'Ta bort gamla kopior och behåll loggar i 90 dagar'}
            color="red"
            onClick={() => setActiveSection('db-cleanup')}
          />
          <AdminCard
            icon={Volume2}
            title={i18n.language === 'ar' ? 'صوت الأسئلة (ElevenLabs)' : 'Frågeljud (ElevenLabs)'}
            desc={i18n.language === 'ar' ? 'إنشاء صوت طبيعي للأسئلة والخيارات مرة واحدة وحفظه' : 'Skapa naturligt ljud för frågor och svar en gång'}
            color="emerald"
            onClick={() => setActiveSection('question-audio')}
          />
          <AdminCard
            icon={Image}
            title={i18n.language === 'ar' ? 'مزامنة الصور' : i18n.language === 'sv' ? 'Bildsynkronisering' : 'Image Sync'}
            desc={i18n.language === 'ar' ? 'تحميل الصور المفقودة من Production (drivemaster.se) إلى Preview' : i18n.language === 'sv' ? 'Ladda ner saknade bilder från produktion till preview' : 'Download missing images from production to preview'}
            color="blue"
            onClick={() => setActiveSection('image-sync')}
          />
          <AdminCard
            icon={Star}
            title={i18n.language === 'ar' ? 'الروابط والمراجعات' : i18n.language === 'sv' ? 'Länkar & Recensioner' : 'Links & Reviews'}
            desc={i18n.language === 'ar' ? 'روابط السوشيال ميديا (يوتيوب، تيك توك، انستغرام...) + رابط مراجعات Google' : i18n.language === 'sv' ? 'Sociala medier (YouTube, TikTok, Instagram...) + Google-recensions-URL' : 'Social media links (YouTube, TikTok, Instagram...) + Google Reviews URL'}
            color="amber"
            onClick={() => setActiveSection('site-settings')}
          />
          <AdminCard
            icon={FileText}
            title={i18n.language === 'ar' ? 'سجل تعديل الأسئلة' : i18n.language === 'sv' ? 'Frågeredigeringshistorik' : 'Question Edit History'}
            desc={i18n.language === 'ar' ? 'سجل كل تعديل + نسخ احتياطية يدوية للأسئلة' : i18n.language === 'sv' ? 'Logg över alla redigeringar + manuella snapshots' : 'Log of every edit + manual snapshot backups'}
            color="emerald"
            onClick={() => setActiveSection('question-history')}
          />
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Admin Header */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 rounded-2xl p-8 text-white shadow-xl mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-10 h-10" />
                <div>
                  <h2 className="text-3xl font-bold">
                    {i18n.language === 'ar' ? 'لوحة الإدارة المتقدمة' : 'Advanced Admin Panel'}
                  </h2>
                  <p className="text-white/90 text-sm">
                    {i18n.language === 'ar' ? 'صلاحيات كاملة لإدارة النظام' : 'Full system management access'}
                  </p>
                </div>
              </div>
              {activeSection !== 'overview' && (
                <button
                  onClick={() => setActiveSection('overview')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all"
                >
                  ← {i18n.language === 'ar' ? 'العودة للنظرة العامة' : 'Back to Overview'}
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {renderContent()}
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      <h3 className="text-3xl font-bold text-slate-900 mb-1">{value}</h3>
      <p className="text-sm font-medium text-slate-500">{label}</p>
    </div>
  );
};

// Admin Card Component
const LiveChatCard = ({ token, i18n, onClick }) => {
  const [stats, setStats] = useState({ total: 0, unread: 0, sos: 0, latest: null });
  const isAr = i18n?.language === 'ar';
  const isSv = i18n?.language === 'sv';

  const fetchStats = React.useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/chat/conversations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const convos = data.conversations || [];
      const unread = convos.filter((c) => c.unread_by_admin).length;
      const sos = convos.filter((c) => c.human_requested).length;
      setStats({ total: convos.length, unread, sos, latest: convos[0] || null });
    } catch (e) {
      /* silent */
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  useVisiblePolling(fetchStats, 60000);

  const hasAttention = stats.unread > 0 || stats.sos > 0;

  return (
    <button
      onClick={onClick}
      className={`relative bg-gradient-to-br ${
        stats.sos > 0
          ? 'from-red-500 via-red-600 to-rose-600'
          : hasAttention
            ? 'from-emerald-500 via-emerald-600 to-teal-600'
            : 'from-emerald-500 via-teal-500 to-cyan-500'
      } text-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-left w-full group overflow-hidden`}
      data-testid="admin-live-chat-card"
    >
      {hasAttention && (
        <span className="absolute inset-0 rounded-2xl ring-4 ring-white/40 animate-pulse pointer-events-none" />
      )}
      <div className="relative flex items-start justify-between mb-3">
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
          <MessageCircle className="w-7 h-7" />
        </div>
        <div className="flex flex-col items-end gap-1">
          {stats.sos > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-white text-red-600 text-[11px] font-black uppercase tracking-wide flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping" />
              {isAr ? `${stats.sos} طلب دعم` : isSv ? `${stats.sos} SOS` : `${stats.sos} SOS`}
            </span>
          )}
          {stats.unread > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-yellow-300 text-slate-900 text-[11px] font-bold">
              {stats.unread} {isAr ? 'جديد' : isSv ? 'nya' : 'new'}
            </span>
          )}
        </div>
      </div>
      <h3 className="text-xl font-black mb-1">
        {isAr ? 'الدعم المباشر' : isSv ? 'Livechatt' : 'Live Chat'}
      </h3>
      <p className="text-sm text-white/90 mb-3 leading-snug">
        {isAr
          ? 'الرد على رسائل الزوار — اسم سويدي مختلف لكل زائر'
          : isSv
            ? 'Svara besökare i realtid — svenskt alias per samtal'
            : 'Reply to visitors in real time — a Swedish alias per chat'}
      </p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-white/10 rounded-lg py-2">
          <div className="text-xl font-black">{stats.total}</div>
          <div className="text-[10px] uppercase tracking-wide opacity-80">
            {isAr ? 'الكل' : isSv ? 'Alla' : 'Total'}
          </div>
        </div>
        <div className="bg-white/10 rounded-lg py-2">
          <div className="text-xl font-black">{stats.unread}</div>
          <div className="text-[10px] uppercase tracking-wide opacity-80">
            {isAr ? 'جديدة' : isSv ? 'Nya' : 'New'}
          </div>
        </div>
        <div className="bg-white/10 rounded-lg py-2">
          <div className="text-xl font-black">{stats.sos}</div>
          <div className="text-[10px] uppercase tracking-wide opacity-80">
            {isAr ? 'SOS' : isSv ? 'SOS' : 'SOS'}
          </div>
        </div>
      </div>
      {stats.latest?.last_message?.text && (
        <div className="mt-3 pt-3 border-t border-white/20 text-[11px] leading-snug opacity-90 truncate">
          <span className="font-bold">{stats.latest.visitor_name || (isAr ? 'زائر' : 'Visitor')}:</span>{' '}
          {stats.latest.last_message.text}
        </div>
      )}
    </button>
  );
};

const AdminCard = ({ icon: Icon, title, desc, color, onClick }) => {
  const colors = {
    blue: 'from-blue-50 to-blue-100 text-blue-600',
    purple: 'from-purple-50 to-purple-100 text-purple-600',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-600',
    amber: 'from-amber-50 to-amber-100 text-amber-600',
    red: 'from-red-50 to-red-100 text-red-600',
    indigo: 'from-indigo-50 to-indigo-100 text-indigo-600',
    teal: 'from-teal-50 to-teal-100 text-teal-600',
    pink: 'from-pink-50 to-pink-100 text-pink-600',
    slate: 'from-slate-50 to-slate-100 text-slate-600',
  };

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-xl hover:border-slate-300 transition-all text-left w-full group"
    >
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-500">{desc}</p>
    </button>
  );
};

export default AdminPanelPage;
